/* ============================================================================
   FILE: assets/comments-customer-autofill.js
   ROLE: Autofill the Better Blog Comments widget with the logged-in
         customer's name and email, then hide those input rows.

   WHY THIS EXISTS
   - UX: Logged-in customers shouldn't re-type name/email for each comment.
   - Safety: Works with main form and dynamically-rendered reply forms.
   - Non-breaking: Fields are hidden (not removed) to avoid validator errors.

   INTEGRATION CONTRACT (from article section)
   - The article section wraps the app block with a host element:
       <div id="comments-app-host"
            data-customer-name="Jane Doe"
            data-customer-email="jane@example.com">
         <!-- Better Blog Comments app block -->
       </div>
   - This script runs only when that host exists AND both data attributes are set.

   WHAT THIS SCRIPT DOES
   1) Injects a tiny stylesheet that hides the name/email rows (scoped to the host).
   2) Finds the app's name/email inputs in both main and reply forms.
   3) Sets values using a React/Vue-safe method, then dispatches input/change.
   4) Hides those input rows (kept in DOM).
   5) Re-applies on:
      • A short retry loop (handles slow/delayed mounts)
      • MutationObserver (handles reply UI toggles and re-renders)

   WHERE TO TWEAK LATER
   - If the app changes its markup, adjust SELECTORS below (containerList, fieldWrapper).
   - If hiding should use a different approach, change HIDE_CLASS and injectScopedStyles().

   NOTE ON COMPATIBILITY
   - Modern browsers supported by Shopify storefronts are expected. No framework required.
   ============================================================================ */

(function () {
  'use strict';

  /* ------------------------------------------
   * Configuration (IDs, selectors, classes)
   * ------------------------------------------ */
  var HOST_ID = 'comments-app-host';

  // Ordered most-specific → most-generic to catch main + reply forms
  var SELECTORS = {
    containerList: [
      '.flexContainer.formDetails',                 // main form (known pattern)
      '.replyContainer .flexContainer.formDetails', // reply variant A
      '.replyForm .flexContainer.formDetails',      // reply variant B
      '.replyContainer .flexContainer',             // reply variant C
      '.replyForm .flexContainer',                  // reply variant D
      '.flexContainer'                              // fallback (last resort)
    ],
    fieldWrapper: '.formField',     // row wrapper to hide (contains the input + label)
    nameInput: 'input[name="name"]',
    emailInput: 'input[name="email"]'
  };

  // Applied to rows we want hidden (scoped to the host via the injected CSS)
  var HIDE_CLASS = 'bb-autofill-hidden-row';

  /* ------------------------------------------
   * Early exits (only run when we have data)
   * ------------------------------------------ */
  var host = document.getElementById(HOST_ID);
  if (!host) return; // Not on an article with comments, or app block not present.

  var customerName = (host.getAttribute('data-customer-name') || '').trim();
  var customerEmail = (host.getAttribute('data-customer-email') || '').trim();
  if (!customerName || !customerEmail) return; // Logged out or data missing.

  /* ------------------------------------------
   * Style injection (scoped to the host)
   * ------------------------------------------ */
  function injectScopedStyles() {
    if (document.getElementById('bb-autofill-style')) return;

    var css =
      '#' + HOST_ID + ' .' + HIDE_CLASS + ' {' +
      '  visibility: hidden !important;' +
      '  opacity: 0 !important;' +
      '  height: 0 !important;' +
      '  margin: 0 !important;' +
      '  padding: 0 !important;' +
      '  pointer-events: none !important;' +
      '  overflow: hidden !important;' +
      '}';

    var style = document.createElement('style');
    style.id = 'bb-autofill-style';
    style.type = 'text/css';
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);
  }

  injectScopedStyles();

  /* ------------------------------------------
   * Small utilities
   * ------------------------------------------ */
  function isEl(n) { return n && n.nodeType === 1; }

  // React/Vue controlled-input safe setter:
  // Use the native property setter so frameworks update internal state.
  function setControlledValue(input, value) {
    if (!isEl(input)) return;

    var proto = Object.getPrototypeOf(input);
    var desc = proto && Object.getOwnPropertyDescriptor(proto, 'value');
    var setter = desc && desc.set;

    try {
      setter ? setter.call(input, value) : (input.value = value);
    } catch (e) {
      input.value = value;
    }

    // Notify validators/state machines
    try {
      input.dispatchEvent(new Event('input',  { bubbles: true, composed: true }));
      input.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    } catch (e) {
      // Older event path fallback
      var ev1 = document.createEvent('Event'); ev1.initEvent('input',  true, false);  input.dispatchEvent(ev1);
      var ev2 = document.createEvent('Event'); ev2.initEvent('change', true, false); input.dispatchEvent(ev2);
    }

    // Prevent further edits and clear error state
    input.readOnly = true;
    input.setAttribute('aria-invalid', 'false');
  }

  function hideFieldRowFor(input) {
    if (!isEl(input)) return;
    var row = input.closest(SELECTORS.fieldWrapper);
    if (row) row.classList.add(HIDE_CLASS);
  }

  /* ------------------------------------------
   * Core processing (one container at a time)
   * ------------------------------------------ */
  function processContainer(container) {
    if (!isEl(container)) return;

    // If we've already applied, only continue when values differ (e.g., app re-rendered)
    if (container.dataset.customerAutofillApplied === 'true') {
      var n0 = container.querySelector(SELECTORS.nameInput);
      var e0 = container.querySelector(SELECTORS.emailInput);
      var same =
        (!n0 || n0.value === customerName) &&
        (!e0 || e0.value === customerEmail);
      if (same) return;
    }

    var nameInput  = container.querySelector(SELECTORS.nameInput);
    var emailInput = container.querySelector(SELECTORS.emailInput);

    if (nameInput) {
      setControlledValue(nameInput, customerName);
      hideFieldRowFor(nameInput);
    }
    if (emailInput) {
      setControlledValue(emailInput, customerEmail);
      hideFieldRowFor(emailInput);
    }

    container.dataset.customerAutofillApplied = 'true';
  }

  function scanAll() {
    // Walk specific → generic to catch both main and reply UIs
    SELECTORS.containerList.forEach(function (sel) {
      host.querySelectorAll(sel).forEach(processContainer);
    });
  }

  /* ------------------------------------------
   * Initialization strategy
   * - Retry loop handles slow/lazy mounting (up to ~9s total).
   * - MutationObserver catches later UI changes (replies, re-renders).
   * ------------------------------------------ */
  var tries = 0, maxTries = 18;

  (function retry() {
    scanAll();
    if (++tries < maxTries) setTimeout(retry, 500);
  })();

  var mo = new MutationObserver(function () {
    scanAll();
  });
  mo.observe(host, { subtree: true, childList: true, attributes: true });

  /* ------------------------------------------
   * Submit button state management and refresh after successful comment submit
   * ------------------------------------------ */
  var COMMENT_SELECTOR = '.individualComment, .replyComment';
  var SUBMIT_SELECTOR = '.submitButton';
  var TEXTAREA_SELECTOR = 'textarea[name="content"]';
  var commentList = host.querySelector('.commentList');
  var baselineCommentCount = commentList ? commentList.querySelectorAll(COMMENT_SELECTOR).length : 0;
  
  // Reload management - single source of truth
  var reloadTimer = null;
  var submissionTrackers = new WeakMap(); // Track which forms/buttons are in submission
  
  function clearReloadTimer() {
    if (reloadTimer) {
      clearTimeout(reloadTimer);
      reloadTimer = null;
    }
  }

  function scheduleReload(delayMs) {
    clearReloadTimer();
    // Only set timer if not already reloading (prevent multiple reloads)
    if (!reloadTimer) {
      reloadTimer = setTimeout(function () {
        reloadTimer = null;
        window.location.reload();
      }, delayMs || 1500); // Default 1.5s gives time for comment to appear
    }
  }

  /* ------------------------------------------
   * Submit button state management based on textarea content
   * ------------------------------------------ */
  function updateSubmitButtonState(textarea, button) {
    if (!textarea || !button) return;

    var content = textarea.value.trim();
    var hasContent = content.length > 0;

    // Disable button if textarea is empty, enable if it has content
    button.disabled = !hasContent;

    // Add visual feedback, but don't interfere with loading states
    // Check if button is in a loading state (has loading-related classes or attributes)
    var isLoading = button.classList.contains('loading') ||
                   button.classList.contains('submitting') ||
                   button.hasAttribute('data-loading') ||
                   button.getAttribute('aria-busy') === 'true';

    if (!isLoading) {
      // Only apply our custom styling when not in loading state
      if (hasContent) {
        button.style.opacity = '1';
        button.style.cursor = 'pointer';
      } else {
        button.style.opacity = '0.5';
        button.style.cursor = 'not-allowed';
      }
    }
    // If loading, let the Better Blog Comments app handle the styling
  }

  function bindTextareaValidation(root) {
    root.querySelectorAll(TEXTAREA_SELECTOR).forEach(function (textarea) {
      if (textarea.dataset.validationBound === 'true') return;
      textarea.dataset.validationBound = 'true';

      // Find the closest submit button (could be in same form or nearby container)
      var container = textarea.closest('.formContainer, .replyContainer, .flexContainer') || textarea.closest('form') || textarea.parentElement;
      var submitButton = container ? container.querySelector(SUBMIT_SELECTOR) : null;

      if (!submitButton) {
        // Fallback: look for submit button anywhere in the host
        submitButton = host.querySelector(SUBMIT_SELECTOR);
      }

      if (submitButton) {
        // Check if this is a reply button (not main comment button)
        // Reply buttons say "Submit", main comment button says "Submit Comment"
        // Reply forms have class "replyForm" on the formContainer div
        var buttonText = (submitButton.textContent || submitButton.innerText || '').trim();
        
        // Check if container has replyForm class or is inside a reply form
        var containerIsReply = container && (
          container.classList && container.classList.contains('replyForm') ||
          container.closest('.replyForm') !== null ||
          container.closest('.replyContainer, .reply') !== null
        );
        
        // Also check if button itself is inside a reply form
        var buttonIsInReply = submitButton.closest('.replyForm, .replyContainer') !== null;
        
        var isReplyButton = buttonText === 'Submit' || containerIsReply || buttonIsInReply;
        
        // Initial state check
        updateSubmitButtonState(textarea, submitButton);

        // Listen for input changes
        textarea.addEventListener('input', function () {
          updateSubmitButtonState(textarea, submitButton);
        });

        // Also listen for keyup for better responsiveness
        textarea.addEventListener('keyup', function () {
          updateSubmitButtonState(textarea, submitButton);
        });

        // Prevent form submission when button is disabled
        var form = textarea.closest('form');
        if (form) {
          form.addEventListener('submit', function (e) {
            if (submitButton.disabled) {
              e.preventDefault();
              e.stopPropagation();
              return false;
            }
          });
        }

        // Store original textarea value to detect when it's cleared after submission
        var originalValue = textarea.value;
        
        // Track this form/textarea pair for submission detection
        var tracker = {
          textarea: textarea,
          button: submitButton,
          container: container,
          submitted: false,
          originalValue: originalValue,
          isReplyButton: isReplyButton
        };
        submissionTrackers.set(submitButton, tracker);
        submissionTrackers.set(textarea, tracker);
        
        // Monitor textarea value clearing (happens after successful submission)
        // Works for both main comment and reply buttons
        var checkTextareaCleared = function() {
          if (tracker.submitted && !textarea.value.trim() && tracker.originalValue.trim()) {
            // Textarea was cleared after submission with original content - likely successful
            scheduleReload(1500);
            return true;
          }
          return false;
        };
        
        // Watch for input events (value property changes) - for all buttons
        textarea.addEventListener('input', function () {
          checkTextareaCleared();
        });
        
        // Handle button clicks - prevent when disabled, mark as submitted when enabled
        submitButton.addEventListener('click', function (e) {
          if (submitButton.disabled) {
            e.preventDefault();
            e.stopPropagation();
            return false;
          } else {
            // Button is enabled, so this is a valid submission attempt
            tracker.submitted = true;
            tracker.originalValue = textarea.value || '';
            
            // Trigger reload logic for both main comment and reply buttons
            // Simplified: Just wait for textarea to clear or reload after timeout
            // Use a single timeout that checks periodically instead of setInterval
            var checkCount = 0;
            var maxChecks = 20; // Check for 6 seconds (20 * 300ms)
            
            function checkForSuccess() {
              checkCount++;
              if (checkTextareaCleared()) {
                return; // Reload will be scheduled by checkTextareaCleared
              }
              
              if (checkCount < maxChecks) {
                setTimeout(checkForSuccess, 300);
              } else {
                // Fallback: reload after 6 seconds if nothing detected
                scheduleReload(0);
              }
            }
            
            // Start checking after a short delay
            setTimeout(checkForSuccess, 500);
            
            // Also set a fallback reload
            clearReloadTimer();
            reloadTimer = setTimeout(function () {
              if (tracker.submitted) {
                reloadTimer = null;
                window.location.reload();
              }
            }, 6000);
          }
        });
      }
    });
  }

  // Initial binding
  bindTextareaValidation(host);

  // Throttle MutationObserver callbacks to prevent performance issues
  var reloadObserverTimeout = null;
  var lastReloadCheck = 0;
  var RELOAD_CHECK_THROTTLE = 500; // Only check every 500ms max

  var commentReloadObserver = new MutationObserver(function (mutations) {
    // Throttle the callback
    var now = Date.now();
    if (now - lastReloadCheck < RELOAD_CHECK_THROTTLE) {
      clearTimeout(reloadObserverTimeout);
      reloadObserverTimeout = setTimeout(function() {
        processReloadMutations(mutations);
      }, RELOAD_CHECK_THROTTLE - (now - lastReloadCheck));
      return;
    }
    lastReloadCheck = now;
    
    processReloadMutations(mutations);
  });

  function processReloadMutations(mutations) {
    // Only bind validation if we detect new textareas (not on every mutation)
    var hasNewTextarea = false;
    for (var i = 0; i < mutations.length && !hasNewTextarea; i++) {
      if (mutations[i].type === 'childList' && mutations[i].addedNodes.length > 0) {
        for (var j = 0; j < mutations[i].addedNodes.length; j++) {
          var node = mutations[i].addedNodes[j];
          if (node.nodeType === 1 && (node.matches && node.matches('textarea') || node.querySelector && node.querySelector('textarea'))) {
            hasNewTextarea = true;
            break;
          }
        }
      }
    }
    if (hasNewTextarea) {
      bindTextareaValidation(host);
    }

    // Update commentList reference in case it was added dynamically
    if (!commentList) {
      commentList = host.querySelector('.commentList');
      if (!commentList) return; // Exit early if no comment list
    }

    // Note: Comment count detection removed - we can't reliably distinguish
    // reply submissions from main comment submissions via count alone.
    // Reload is now handled solely by reply button detection in click handlers.
  }

  commentReloadObserver.observe(host, { childList: true, subtree: true, attributes: false });

  /* ------------------------------------------
   * Monitor button state changes to handle loading states and detect submission completion
   * Simplified to reduce overhead - only check when needed
   * ------------------------------------------ */
  var buttonStateObserverTimeout = null;
  
  var buttonStateObserver = new MutationObserver(function (mutations) {
    // Throttle button state checks
    clearTimeout(buttonStateObserverTimeout);
    buttonStateObserverTimeout = setTimeout(function() {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'attributes' &&
            (mutation.attributeName === 'class' || 
             mutation.attributeName === 'data-loading' || 
             mutation.attributeName === 'aria-busy')) {
          var button = mutation.target;
          if (button && button.matches && button.matches(SUBMIT_SELECTOR)) {
            var tracker = submissionTrackers.get(button);
            
            // If button finished loading and was submitted, check for textarea clearing
            // Works for both main comment and reply buttons
            if (tracker && tracker.submitted) {
              var isLoading = button.classList.contains('loading') ||
                             button.classList.contains('submitting') ||
                             button.hasAttribute('data-loading') ||
                             button.getAttribute('aria-busy') === 'true';
              
              // If button is no longer loading, check if textarea was cleared
              if (!isLoading) {
                var textarea = tracker.textarea;
                if (textarea && !textarea.value.trim() && tracker.originalValue.trim()) {
                  scheduleReload(1500);
                }
              }
            }
            
            // Re-evaluate button state (find textarea once per check)
            var container = button.closest('.formContainer, .replyContainer, .flexContainer');
            if (container) {
              var textarea = container.querySelector(TEXTAREA_SELECTOR);
              if (textarea) {
                updateSubmitButtonState(textarea, button);
              }
            }
          }
        }
      });
    }, 100); // Throttle to 100ms
  });

  buttonStateObserver.observe(host, {
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'data-loading', 'aria-busy'] // Removed 'style' and 'disabled' to reduce events
  });
})();
